import { useMemo } from "react";
import { Vector2 } from "three";
import { useBodyPalette } from "../../scene/BodyFinish";
import { Chassis } from "../../scene/Chassis";
import { GLASS, HEADLIGHT, TAIL_LIGHT } from "../../scene/materials";
import { MIXER_10, MIXER_6, lastAxle } from "./dimensions";
const CHASSIS_RAIL_Z = 0.44;
const RAIL_DEPTH = 0.26;
const drumProfile = (d) => [
  new Vector2(d.frontRadius, -d.length / 2),
  new Vector2(d.midRadius, 0),
  new Vector2(d.rearRadius, d.length / 2)
];
function MixerBody({ dimensions: d }) {
  const palette = useBodyPalette("mixer");
  const profile = useMemo(() => drumProfile(d.drum), [d.drum]);
  const nose = d.length / 2;
  const tail = -nose;
  const railCentreY = d.chassisY - RAIL_DEPTH / 2;
  const railFront = nose - 0.5;
  const railRear = tail + 0.1;
  const cabCentre = nose - d.cabLength / 2;
  const cabHeight = d.cabRoofY - d.cabFloorY;
  const cabCentreY = d.cabFloorY + cabHeight / 2;
  const rearmost = lastAxle(d);
  return <group>
      <Chassis>
        {[-CHASSIS_RAIL_Z, CHASSIS_RAIL_Z].map((z) => <mesh key={`rail-${z}`} position={[(railFront + railRear) / 2, railCentreY, z]}>
            <boxGeometry args={[railFront - railRear, RAIL_DEPTH, 0.1]} />
            <meshPhysicalMaterial {...palette.chassis} />
          </mesh>)}
        {d.crossmemberX.map((x) => <mesh key={`crossmember-${x}`} position={[x, railCentreY, 0]}>
            <boxGeometry args={[0.08, 0.16, CHASSIS_RAIL_Z * 2]} />
            <meshPhysicalMaterial {...palette.chassis} />
          </mesh>)}
      </Chassis>

      <mesh position={[cabCentre, cabCentreY, 0]}>
        <boxGeometry args={[d.cabLength, cabHeight, d.cabWidth]} />
        <meshPhysicalMaterial {...palette.body} />
      </mesh>
      <mesh position={[nose + 0.02, d.cabFloorY + cabHeight * 0.62, 0]}>
        <boxGeometry args={[0.06, cabHeight * 0.42, d.cabWidth - 0.28]} />
        <meshPhysicalMaterial {...GLASS} />
      </mesh>
      {[-1, 1].map((side) => <mesh
    key={`side-window-${side}`}
    position={[cabCentre + 0.1, d.cabFloorY + cabHeight * 0.62, side * (d.cabWidth / 2 + 4e-3)]}
  >
          <boxGeometry args={[d.cabLength * 0.55, cabHeight * 0.38, 0.02]} />
          <meshPhysicalMaterial {...GLASS} />
        </mesh>)}
      <mesh position={[nose + 0.04, d.cabFloorY + 0.86, 0]}>
        <boxGeometry args={[0.1, 0.55, d.cabWidth - 0.5]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      {[-1, 1].map((side) => <mesh
    key={`headlight-${side}`}
    position={[nose + 0.05, d.cabFloorY + 0.4, side * (d.cabWidth / 2 - 0.34)]}
  >
          <boxGeometry args={[0.1, 0.22, 0.4]} />
          <meshPhysicalMaterial {...HEADLIGHT} />
        </mesh>)}
      <mesh position={[nose + 0.06, 0.68, 0]}>
        <boxGeometry args={[0.18, 0.26, d.cabWidth + 0.02]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      {[-1, 1].map((side) => <group key={`mirror-${side}`}>
          <mesh position={[nose - 0.18, d.cabRoofY - 0.55, side * (d.cabWidth / 2 + 0.09)]}>
            <boxGeometry args={[0.05, 0.05, 0.2]} />
            <meshPhysicalMaterial {...palette.edge} />
          </mesh>
          <mesh position={[nose - 0.22, d.cabRoofY - 0.72, side * (d.cabWidth / 2 + 0.21)]}>
            <boxGeometry args={[0.09, 0.4, 0.15]} />
            <meshPhysicalMaterial {...palette.edge} />
          </mesh>
        </group>)}

      <group position={[d.drum.centreX, d.drum.centreY, 0]} rotation={[0, 0, d.drum.rake]}>
        <mesh rotation={[0, 0, Math.PI / 2]}>
          <latheGeometry args={[profile, 48]} />
          <meshPhysicalMaterial {...palette.secondary} />
        </mesh>
        {[-0.9, 0, 0.9].map((along) => <mesh key={`band-${along}`} position={[along * (d.drum.length / 2.6), 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[d.drum.midRadius + 0.03, d.drum.midRadius + 0.03, 0.08, 32]} />
            <meshPhysicalMaterial {...palette.edge} />
          </mesh>)}
      </group>

      {
    /* Rear pedestal, rollers, hopper, chute, water tank, ladder. */
  }
      <mesh position={[d.drum.centreX - d.drum.length * 0.38, d.chassisY + 0.55, 0]}>
        <boxGeometry args={[0.55, 1.1, 1.15]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      {[-1, 1].map((side) => <mesh
    key={`roller-${side}`}
    position={[d.drum.centreX + 0.4, d.chassisY + 0.42, side * 0.72]}
    rotation={[Math.PI / 2, 0, 0]}
  >
          <cylinderGeometry args={[0.12, 0.12, 0.28, 16]} />
          <meshPhysicalMaterial {...palette.edge} />
        </mesh>)}
      <mesh
    position={[d.drum.centreX - d.drum.length * 0.48, d.drum.centreY + 1.05, 0]}
    rotation={[0, 0, -0.4]}
  >
        <boxGeometry args={[0.9, 0.55, 0.85]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      <mesh
    position={[d.drum.centreX - d.drum.length * 0.52, d.chassisY + 0.15, 0.55]}
    rotation={[0.35, 0, -0.55]}
  >
        <boxGeometry args={[1.15, 0.12, 0.28]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      <Chassis>
        <mesh position={[0.55, railCentreY + 0.12, -0.82]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.28, 0.28, 1.1, 24]} />
          <meshPhysicalMaterial {...palette.edge} />
        </mesh>
        {[-1, 1].map((side) => <mesh
    key={`mudflap-${side}`}
    position={[rearmost.x - d.wheelRadius - 0.16, 0.5, side * d.dualHalfTrack]}
  >
            <boxGeometry args={[0.03, 0.36, 0.66]} />
            <meshPhysicalMaterial {...palette.chassis} />
          </mesh>)}
        <mesh position={[tail + 0.12, 0.58, 0]}>
          <boxGeometry args={[0.1, 0.14, 2.1]} />
          <meshPhysicalMaterial {...palette.chassis} />
        </mesh>
      </Chassis>
      {[-0.15, 0.2, 0.55, 0.9].map((y) => <mesh key={`rung-${y}`} position={[d.drum.centreX + 0.15, d.chassisY + y, 1.12]}>
          <boxGeometry args={[0.04, 0.04, 0.42]} />
          <meshPhysicalMaterial {...palette.edge} />
        </mesh>)}
      <mesh position={[d.drum.centreX + 0.15, d.chassisY + 0.5, 1.32]}>
        <boxGeometry args={[0.05, 1.15, 0.05]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      <mesh position={[d.drum.centreX + 0.15, d.chassisY + 0.5, 0.92]}>
        <boxGeometry args={[0.05, 1.15, 0.05]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      {[-1, 1].map((side) => <mesh key={`tail-light-${side}`} position={[tail + 0.08, 0.95, side * 0.85]}>
          <boxGeometry args={[0.09, 0.22, 0.28]} />
          <meshPhysicalMaterial {...TAIL_LIGHT} />
        </mesh>)}
      {[-0.5, 0, 0.5].map((z) => <mesh key={`marker-${z}`} position={[nose - 0.4, d.cabRoofY + 0.05, z]}>
          <boxGeometry args={[0.08, 0.06, 0.12]} />
          <meshPhysicalMaterial {...palette.accent} />
        </mesh>)}
    </group>;
}
const Mixer10Body = () => <MixerBody dimensions={MIXER_10} />;
const Mixer6Body = () => <MixerBody dimensions={MIXER_6} />;
export {
  Mixer10Body,
  Mixer6Body
};
